import type { SortPayload } from "ra-core";

import type { Company } from "../types";

/**
 * Accounts are triaged by lateness first, so the list opens on the accounts
 * with the most overdue tasks.
 */
export const DEFAULT_ACCOUNTS_SORT: SortPayload = {
  field: "nb_overdue_tasks",
  order: "DESC",
};

/**
 * Apply the secondary `next_task_due ASC` of the default order.
 *
 * The data provider orders on a single column, so the tie-break is applied to
 * the fetched page here. Ties straddling a page boundary keep the server order,
 * which is why this only runs while the default sort is active.
 */
export const applyDefaultTieBreak = (
  companies: Company[],
  sort: SortPayload,
): Company[] => {
  if (
    sort.field !== DEFAULT_ACCOUNTS_SORT.field ||
    sort.order !== DEFAULT_ACCOUNTS_SORT.order
  ) {
    return companies;
  }

  return [...companies].sort((a, b) => {
    const byOverdue = (b.nb_overdue_tasks ?? 0) - (a.nb_overdue_tasks ?? 0);
    if (byOverdue !== 0) return byOverdue;
    // An account with no next action sorts last: there is nothing to do on it.
    if (!a.next_task_due) return b.next_task_due ? 1 : 0;
    if (!b.next_task_due) return -1;
    return a.next_task_due.localeCompare(b.next_task_due);
  });
};
